import { Flex, Box, Text, UploadCSV, DownloadIcon } from "@shared/primitive-components"
import { navigate } from "@reach/router"
import { useReducer } from "react"
import * as styles from "./styles"
import { Feedback } from "../feedback/Feedback"
import { isValidCSV, parseCSV } from "./utils"
import { useAccess, usePolicyAndFleet } from "../../../../contexts"
import { useFleetEntryValidation } from "../../../validatation/useFleetEntryValidation"
import { CSVErrors } from "./csvErrors"
import {
  fleetEntryCSVUploadReducer,
  fleetEntryCSVUploadInitialState,
  FleetEntryCSVUploadState,
} from "./fleetEntryCSVUploadReducer"
import { AddedFleetEntry } from "../../../../api/fleet"
import { HAVEN_ID } from "../../../../const/policies"

export const FLEET_ENTRY_CSV_UPLOAD_DTI = {
  FILE_ERROR: `fleet_entry_csv_upload_file_error`,
  ERRORS: `fleet_entry_csv_upload_errors`,
}

// Passing initial state is enabled in order to facilitate testing
export type FleetEntryCSVUploadProps = {
  dataTestId?: string
  initialState?: FleetEntryCSVUploadState
  onValidEntriesAdded: (entries: ReadonlyArray<AddedFleetEntry>) => void
}

export const FleetEntryCSVUpload = ({ dataTestId, initialState, onValidEntriesAdded }: FleetEntryCSVUploadProps) => {
  const { policy, fleet } = usePolicyAndFleet()
  const isHavenPolicy = policy.underwriter.id === HAVEN_ID
  const isCourier = policy.meta?.segment === `courier`
  const hasAdminAccess = useAccess().hasAdminAccess()
  const [state, dispatch] = useReducer(fleetEntryCSVUploadReducer, initialState || fleetEntryCSVUploadInitialState)
  const { validateRows } = useFleetEntryValidation(
    fleet.data || [],
    { startTime: policy.startTime, endTime: policy.endTime },
    hasAdminAccess,
    isHavenPolicy,
    isCourier
  )
  const onUpload = (csv: unknown[]) => {
    if (!isValidCSV(csv)) {
      dispatch({ type: `setFileError`, error: `Please use a csv file that matches the supplied template` })
      return
    }
    const parsed = parseCSV(csv)
    if (typeof parsed === `string`) {
      dispatch({ type: `setFileError`, error: parsed })
      return
    }
    const errors = validateRows(parsed)
    if (errors.find((error) => !!Object.values(error).find((it) => !!it))) {
      dispatch({ type: `setEntries`, entries: parsed })
      dispatch({ type: `setErrors`, errors })
      return
    }
    onValidEntriesAdded(parsed as unknown as ReadonlyArray<AddedFleetEntry>)
  }
  const reset = () => dispatch({ type: `reset` })
  if (state.fileError)
    return (
      <Feedback
        summary="CSV file upload failed"
        additionalMessage={<Text>{state.fileError}</Text>}
        additionalMessageOffset
        onFinish={reset}
        status="error"
        dataTestId={FLEET_ENTRY_CSV_UPLOAD_DTI.FILE_ERROR}
      />
    )
  if (state.errors.length)
    return (
      <CSVErrors
        errors={state.errors}
        rows={state.entries}
        onFinish={reset}
        dataTestId={FLEET_ENTRY_CSV_UPLOAD_DTI.ERRORS}
      />
    )
  return (
    <Box data-testid={dataTestId}>
      <Text>
        Use this functionality to add multiple vehicles to your policy at once. Just upload new vehicles here and
        we&apos;ll make sure your cover is updated and everything is submitted to the Motor Insurance Database on your
        behalf.
      </Text>
      <Flex css={styles.instructions}>
        <DownloadIcon />
        <Text
          tag="a"
          css={styles.link}
          href="https://flock-cover-static.s3.eu-west-2.amazonaws.com/Flock-Motor-Fleet-Management-Portal-Example-CSV.csv"
        >
          Download template
        </Text>
      </Flex>
      <UploadCSV uploadData={onUpload} cancelButton={{ text: `Cancel`, action: () => navigate(`fleet`) }} />
    </Box>
  )
}

FleetEntryCSVUpload.defaultProps = {
  dataTestId: undefined,
  initialState: fleetEntryCSVUploadInitialState,
}
